import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Monitor, Globe, Trash2, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Header } from '../components/Header/Header';
import {
  useSessionsQuery,
  useRevokeSessionMutation,
  useRevokeOtherSessionsMutation,
} from '../hooks/useSessionsQuery';
import type { UserSessionDto } from '../hooks/useSessionsApi';

/**
 * Format a UA string into a short device label.
 *
 * Heuristic-based — recognises the common platforms (macOS / Windows / Linux /
 * iOS / Android) and the major browsers. Falls back to the raw UA when the
 * string doesn't match a known pattern. We deliberately keep this simple:
 * the goal is a UI hint, not forensic accuracy.
 */
function formatUserAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';

  let platform = 'Unknown OS';
  if (/Macintosh|Mac OS X/i.test(ua)) platform = 'macOS';
  else if (/Windows/i.test(ua)) platform = 'Windows';
  else if (/Android/i.test(ua)) platform = 'Android';
  else if (/iPhone|iPad/i.test(ua)) platform = 'iOS';
  else if (/Linux/i.test(ua)) platform = 'Linux';

  let browser = '';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Safari';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/curl\//i.test(ua)) browser = 'curl';

  return browser ? `${platform} · ${browser}` : platform;
}

/** Format an ISO-8601 timestamp as a friendly relative-ish display. */
function formatLastActive(iso: string): string {
  const date = new Date(iso);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return date.toLocaleString();
}

/**
 * Sessions page — lists every active `UserSession` for the current user and
 * lets them revoke individual sessions or every other session in one click.
 *
 * @see HZVT-149_ADD_AUTH_SESSION_MANAGEMENT
 */
export const SessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: sessions = [], isLoading } = useSessionsQuery();
  const revokeOne = useRevokeSessionMutation();
  const revokeOthers = useRevokeOtherSessionsMutation();

  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  const handleRevokeOne = async (session: UserSessionDto) => {
    try {
      await revokeOne.mutateAsync(session.id);
      toast.success(t('sessions.revokeSuccess', 'Session revoked'));
    } catch {
      toast.error(t('sessions.revokeError', 'Failed to revoke session'));
    }
  };

  const handleRevokeOthers = async () => {
    setConfirmRevokeAll(false);
    try {
      const { revokedCount } = await revokeOthers.mutateAsync();
      toast.success(t('sessions.revokeOthersSuccess', `Revoked ${revokedCount} other session(s)`));
    } catch {
      toast.error(t('sessions.revokeOthersError', 'Failed to revoke other sessions'));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto max-w-4xl p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back', 'Back')}
          </Button>
          <h1 className="text-2xl font-bold">{t('sessions.title', 'Active sessions')}</h1>
          <div /> {/* spacer */}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('sessions.headerTitle', 'Where you are signed in')}</CardTitle>
            <CardDescription>
              {t(
                'sessions.headerDesc',
                'Each session represents a browser or device that has logged into your account. Revoke a session to immediately stop it from refreshing tokens.',
              )}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('sessions.loading', 'Loading sessions…')}
              </p>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('sessions.empty', 'No active sessions found.')}
              </p>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-start justify-between rounded-lg border p-4"
                    data-testid={`session-row-${session.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Monitor className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div className="space-y-1">
                        <div className="font-medium">{formatUserAgent(session.userAgent)}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Globe className="h-3 w-3" />
                          {session.ipAddress ?? t('sessions.unknownIp', 'Unknown IP')}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {t('sessions.lastActive', 'Last active')}:{' '}
                          {formatLastActive(session.lastActiveAt)}
                        </div>
                        {session.orgId && (
                          <Badge variant="secondary" className="text-xs mt-1">
                            org: {session.orgId.slice(0, 8)}…
                          </Badge>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeOne(session)}
                      disabled={revokeOne.isPending}
                      aria-label={t('sessions.revoke', 'Revoke session')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {sessions.length > 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldOff className="h-5 w-5" />
                {t('sessions.revokeOthersTitle', 'Sign out of other devices')}
              </CardTitle>
              <CardDescription>
                {t(
                  'sessions.revokeOthersDesc',
                  'Sign out of every session except the one you are currently using. Useful if you suspect a stolen device or shared computer.',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {confirmRevokeAll ? (
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={handleRevokeOthers}
                    disabled={revokeOthers.isPending}
                  >
                    {t('sessions.confirmRevokeOthers', 'Yes, revoke all other sessions')}
                  </Button>
                  <Button variant="outline" onClick={() => setConfirmRevokeAll(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setConfirmRevokeAll(true)}
                  disabled={revokeOthers.isPending}
                >
                  {t('sessions.revokeOthersAction', 'Revoke other sessions')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};
